import type { CSSProperties } from "react";
import type { RadarBubble, RadarZones } from "@/services/api/teacher";
import {
  ATTENTION_RGB,
  BREATHE_PERIOD_MS,
  FROST_EDGE,
  FROST_GLYPH,
  FROST_GLOW,
  FROST_RING,
  FROST_VEIL_INNER,
  FROST_VEIL_MID,
  FROST_VEIL_OUTER,
  calculateBadgeFontSize,
  calculateBadgeOffset,
  calculateBadgeRadius,
  calculateVisualRadius,
  getGlowColor,
  resolveAttention,
  type RadarSizeConfig,
} from "@/components/radar/canvas/BubbleRenderer";
import { darken, lighten } from "@/components/radar/utils/colors";

export type RadarDotZone = "panic" | "middle" | "outer";

const FALLBACK_ZONES: RadarZones = {
  panic_zone: { threshold_hours: 2, type: "fixed", color: "", opacity: 0.2, label: "" },
  middle_zone: { threshold_hours: 48, type: "fixed", color: "", opacity: 0.2, label: "" },
  outer_zone: { threshold_hours: null, type: "fixed", color: "", opacity: 0.2, label: "" },
};

const ZONE_COLOR: Record<RadarDotZone, string> = {
  panic: "#ef4444",
  middle: "#f59e0b",
  outer: "#94a3b8",
};

export function radarDotZone(bubble: RadarBubble): RadarDotZone {
  if (bubble.is_in_panic_zone) return "panic";
  if (bubble.is_in_middle_zone) return "middle";
  return "outer";
}

function fillColor(bubble: RadarBubble, zone: RadarDotZone): string {
  return bubble.bubble_color || ZONE_COLOR[zone];
}

function resolvedStatus(bubble: RadarBubble): boolean {
  return (
    bubble.status === "accepted" ||
    bubble.status === "filled" ||
    bubble.status === "booked"
  );
}

export type RadarBubbleFace = {
  incoming: boolean;
  frost: boolean;
  color: string;
  diameter: number;
  bodyStyle: CSSProperties;
  highlight: { size: number; offset: number } | null;
  tintSize: number;
  attention: { diameter: number; color: string; durationMs: number } | null;
  prep: { diameter: number; durationMs: number } | null;
  frostEdge: number;
  frostGlyph: number;
  frostVeil: string;
  frostRing: { diameter: number; color: string } | null;
  resolved: { outer: number; inner: number } | null;
  badge: { text: string; size: number; offset: number; font: number } | null;
};

export function radarCardKind(
  bubbleType?: string | null
): "incoming" | "own" {
  return bubbleType === "incoming" ? "incoming" : "own";
}

export function radarBubbleFace(
  bubble: RadarBubble,
  zone: RadarDotZone,
  discRadius: number,
  sizeConfig: RadarSizeConfig | null,
  zones: RadarZones | null
): RadarBubbleFace {
  const color = fillColor(bubble, zone);
  const incoming = bubble.bubble_type === "incoming";
  const frost = !!bubble.gate_pending && !incoming;
  const resolved = resolvedStatus(bubble);
  const maxRadius = Math.max(0, discRadius);
  const source = Number.isFinite(bubble.bubble_radius) ? bubble.bubble_radius : 20;
  const radius = calculateVisualRadius(source, maxRadius, sizeConfig, false);
  const glow = frost
    ? FROST_GLOW
    : getGlowColor({ ...bubble, bubble_color: color }, zones ?? FALLBACK_ZONES);
  const blur = (frost ? 0.5 : 1) * Math.min(incoming ? 18 : 12, radius * 0.5);
  const attention = resolveAttention(bubble);
  const ringOffset = attention ? Math.max(8, radius * 0.15) : 0;
  const prepOffset = !attention && bubble.prep_open ? Math.max(4, radius * 0.1) : 0;
  const frostGap = frost ? (radius >= 12 ? 5 : 3) : 0;
  const badgeText = bubble.lesson_count > 1 ? String(bubble.lesson_count) : "";
  const badgeRadius = badgeText ? calculateBadgeRadius(radius, false) : 0;

  return {
    incoming,
    frost,
    color,
    diameter: radius * 2,
    bodyStyle: {
      backgroundColor: incoming ? "rgba(255, 255, 255, 0.95)" : color,
      backgroundImage: incoming
        ? undefined
        : `radial-gradient(circle at 32% 28%, ${lighten(color, 35)} 0%, ${color} 42%, ${darken(color, 15)} 100%)`,
      border: incoming ? `3px solid ${color}` : undefined,
      boxShadow: `0 0 ${blur}px ${glow}`,
      width: radius * 2,
      height: radius * 2,
      ["--bubble" as string]: color,
    },
    highlight: incoming
      ? null
      : {
          size: Math.min(radius * 0.25, 10) * 2,
          offset: Math.min(radius * 0.35, 12),
        },
    tintSize: Math.min(radius * 0.5, 20) * 2,
    attention: attention
      ? {
          diameter: (radius + ringOffset) * 2,
          color: `rgba(${ATTENTION_RGB[attention].r}, ${ATTENTION_RGB[attention].g}, ${ATTENTION_RGB[attention].b}, 0.8)`,
          durationMs: BREATHE_PERIOD_MS,
        }
      : null,
    prep: prepOffset
      ? { diameter: (radius + prepOffset) * 2, durationMs: BREATHE_PERIOD_MS }
      : null,
    frostEdge: Math.max(2, Math.min(2.5, radius * 0.1)),
    frostGlyph: Math.max(7, radius * 0.55),
    frostVeil: `radial-gradient(circle at 30% 28%, ${FROST_VEIL_INNER}, ${FROST_VEIL_MID} 55%, ${FROST_VEIL_OUTER})`,
    frostRing: frost
      ? { diameter: (radius + frostGap) * 2, color: FROST_RING }
      : null,
    resolved:
      resolved && !frost
        ? {
            outer: Math.max(radius * 0.35, 6) * 2,
            inner: Math.max(radius * 0.15, 3) * 2,
          }
        : null,
    badge: badgeText
      ? {
          text: badgeText,
          size: badgeRadius * 2,
          offset: calculateBadgeOffset(radius),
          font: calculateBadgeFontSize(badgeRadius, badgeText, false),
        }
      : null,
  };
}

export { FROST_EDGE, FROST_GLYPH };
